import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRemoveOrganizationMutation } from '../../../types/generated-types';
import {
  MaterialReactTable,
  MRT_VisibilityState,
  MRT_ColumnDef,
} from 'material-react-table';
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Visibility } from '@mui/icons-material';
import { Notifier } from '../../system/services/notificationManager';
import { useInjectableComponents } from '../../system/services/injectableComponentsManager';
import { BottomMenuItem } from '../../system/services/injectableComponentsManager';
import { useAuthorizer } from '../../auth/AuthorizationContext';
import {
  useOrganizations,
  ManagedOrganization,
} from '../../system/EntityManager';
import {
  useIsSmallScreen,
  useIsMediumScreen,
} from '../../system/SystemMediaQueriesProvider';

type MyOrganization = Partial<ManagedOrganization>;

export const OrganizationList = () => {
  const navigate = useNavigate();
  const isSmall = useIsSmallScreen();
  const isMedium = useIsMediumScreen();
  const { can } = useAuthorizer();
  const [organizations, setOrganizations] = useState<MyOrganization[]>([]);

  // const { data, error, loading } = useOrganizationListQuery();
  // useOrganizationListSubSubscription({
  //   onData: updateCacheFromSubscriptionEvent,
  //   fetchPolicy: 'no-cache',
  // });

  const { organizations: myOrganizations, loading, error } = useOrganizations();

  const [deleteOrganization] = useRemoveOrganizationMutation();

  useEffect(() => {
    setOrganizations(myOrganizations ? myOrganizations : []);
  }, [myOrganizations]);

  useEffect(() => {
    if (error) {
      Notifier.error('There was a problem fetching the organizations: ', error);
    }
  }, [error]);

  const handleRemoveOrganization = useCallback(
    async (id: string) => {
      if (!can('delete', 'Organization')) {
        Notifier.error('You do not have permission to delete organizations');
        return;
      }

      try {
        await deleteOrganization({
          variables: {
            id,
          },
        });

        Notifier.success('Organization removed');
      } catch (e) {
        Notifier.error('Error removing Organization');
        console.error(e);
      }
    },
    [deleteOrganization, can],
  );

  const { setContextMenuItems } = useInjectableComponents();

  useEffect(() => {
    const items: BottomMenuItem[] = [];

    if (can('create', 'Organization')) {
      items.push({
        id: 'add-organization-menu-item',
        label: 'Add Organization',
        icon: <Add fontSize="small" />,
        navTarget: '/organizations/add-organization',
      });
    }

    setContextMenuItems(items.length > 0 ? [{ items }] : undefined);

    return () => {
      setContextMenuItems(undefined);
    };
  }, [setContextMenuItems, can]);

  // Set initial visibility based on screen size
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {
      'parentOrganization.name': !(isSmall || isMedium),
    },
  );

  // Update column visibility when screen size changes
  useEffect(() => {
    setColumnVisibility((prev) => ({
      ...prev,
      'parentOrganization.name': !(isSmall || isMedium),
    }));
  }, [isSmall, isMedium]);

  const columns = React.useMemo<MRT_ColumnDef<MyOrganization>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'parentOrganization.name',
        header: 'Parent Organization',
      },
      {
        accessorKey: 'hqAddress.city',
        header: 'HQ City',
      },
      {
        accessorKey: 'hqPhone',
        header: 'Phone',
        size: 150,
        Cell: ({ cell }) => {
          const phone = cell.getValue<string>();
          return (
            <a
              href={`tel:${phone}`}
              style={{ textDecoration: 'none', color: '#36AFA5' }}
            >
              {phone}
            </a>
          );
        },
      },
      {
        accessorKey: 'hqEmail',
        header: 'Email',
        Cell: ({ cell }) => {
          const email = cell.getValue<string>();
          return (
            <a
              href={`mailto:${email}`}
              style={{
                textDecoration: 'none',
                color: '#36AFA5',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {email}
            </a>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 160,
        grow: false,
        Cell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              gap: '0.5rem',
              p: '0.5rem',
              maxWidth: 150,
            }}
          >
            <Tooltip title="View">
              <IconButton
                onClick={() =>
                  navigate(
                    `/organizations/${row.original._id}/view-organization`,
                  )
                }
              >
                <Visibility />
              </IconButton>
            </Tooltip>
            {can('update', 'Organization') && (
              <Tooltip title="Edit">
                <IconButton
                  onClick={() =>
                    navigate(
                      `/organizations/${row.original._id}/edit-organization`,
                    )
                  }
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {can('delete', 'Organization') && (
              <Tooltip title="Delete">
                <IconButton
                  onClick={() =>
                    row.original._id &&
                    handleRemoveOrganization(row.original._id)
                  }
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [navigate, handleRemoveOrganization, can],
  );

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box width={'100%'}>
      <MaterialReactTable
        columns={columns}
        data={organizations}
        defaultColumn={{ size: 180 }}
        layoutMode="grid"
        state={{
          isLoading: loading,
          columnVisibility,
        }}
        onColumnVisibilityChange={setColumnVisibility}
        enableColumnFilters={false}
        enableColumnActions={false}
        enableColumnResizing={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableRowActions={false}
        enableRowSelection={false}
        enableTopToolbar={false}
        muiTableBodyRowProps={{ hover: false }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: 'bold',
            fontSize: '14px',
            p: '0.5rem',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '14px',
            p: '0.5rem',
          },
        }}
        initialState={{
          columnVisibility: {
            'parentOrganization.name': !(isSmall || isMedium),
          } as MRT_VisibilityState,
        }}
      />
    </Box>
  );
};
